import React, { useState, useEffect, useRef } from "react";
import { getDatabase, ref, get, set, onValue } from "firebase/database";
import { auth } from "../../firebase";
import {
  getFortpixelProjectsPath,
  getFortpixelUserInputsPath,
} from "../../utils/fortpixelData";
import JsonModal from "./JsonViewer";
import BulkAddModal from "./BulkAddModal";
import DuplicateModal from "./DuplicateModal";
import VirtualList from "./VirtualList";

const DataManager = ({ isAdmin }) => {
  const [currentProject, setCurrentProject] = useState("");
  const [projects, setProjects] = useState({});
  const [newItemId, setNewItemId] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [error, setError] = useState("");
  const [debugLog, setDebugLog] = useState([]);
  const [inputCount, setInputCount] = useState(0);
  const [lastReset, setLastReset] = useState(null);
  const nameInputRef = useRef(null);

  const addToDebugLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      message,
      data: data ? JSON.stringify(data, null, 2) : null,
    };
    setDebugLog((prevLog) => [...prevLog, logEntry]);
  };

  // Track daily inputs
  useEffect(() => {
    if (!auth.currentUser) return;

    const db = getDatabase();
    const userInputRef = ref(
      db,
      getFortpixelUserInputsPath(auth.currentUser.uid),
    );

    const checkDailyReset = async () => {
      const today = new Date().toISOString().split("T")[0];

      const snapshot = await get(userInputRef);
      const userData = snapshot.val() || { count: 0, lastReset: "" };

      if (userData.lastReset !== today) {
        // Reset count for new day
        await set(userInputRef, {
          count: 0,
          lastReset: today,
        });
        setInputCount(0);
        setLastReset(today);
      } else {
        setInputCount(userData.count || 0);
        setLastReset(userData.lastReset);
      }
    };

    checkDailyReset();

    // Listen for changes to input count
    const unsubscribe = onValue(userInputRef, (snapshot) => {
      const data = snapshot.val() || { count: 0 };
      setInputCount(data.count);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const db = getDatabase();
    const dataRef = ref(db, getFortpixelProjectsPath());
    let unsubscribe = () => {};

    const initialize = async () => {
      // Test database connectivity
      try {
        const testRef = ref(db, ".info/connected");
        const snapshot = await get(testRef);
        addToDebugLog("Database connection test", {
          connected: snapshot.val(),
          database: db.app.options.databaseURL,
        });
      } catch (err) {
        addToDebugLog("Database connection test failed", {
          error: err.message,
        });
      }

      addToDebugLog("Initializing database connection", {
        path: dataRef.toString(),
        auth: !!auth.currentUser,
      });

      unsubscribe = onValue(
        dataRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setProjects(data);
          } else if (!snapshot.exists()) {
            const defaultData = {
              "Mining Tycoon": {},
            };
            set(dataRef, defaultData)
              .then(() => {
                setProjects(defaultData);
                setCurrentProject("Mining Tycoon");
              })
              .catch((err) => {
                setError("Failed to initialize data: " + err.message);
                console.error(err);
              });
          }
        },
        (error) => {
          setError("Failed to load data: " + error.message);
          console.error(error);
        },
      );
    };

    initialize().catch((error) => {
      setError("Failed to initialize fortpixel data: " + error.message);
      console.error(error);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (projects && Object.keys(projects).length > 0) {
      if (!currentProject || !projects[currentProject]) {
        setCurrentProject(Object.keys(projects)[0]);
      }
    }
  }, [projects, currentProject]);

  const saveData = async (newData) => {
    const db = getDatabase();
    const dataRef = ref(db, getFortpixelProjectsPath());
    setDebugLog([]);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      addToDebugLog("Authentication state", {
        isAuthenticated: true,
        userId: currentUser.uid,
      });

      if (!newData || Object.keys(newData).length === 0) {
        throw new Error("Cannot save empty data");
      }

      addToDebugLog("Save preparation", {
        currentState: projects,
        newState: newData,
      });

      // Write the data
      await set(dataRef, newData);
      addToDebugLog("Initial write completed");

      // Add a delay before verification
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Verify with multiple attempts
      let savedData = null;
      let attempts = 3;

      while (attempts > 0 && !savedData) {
        addToDebugLog(`Verification attempt ${4 - attempts}`);
        const snapshot = await get(dataRef);
        savedData = snapshot.val();

        if (!savedData && attempts > 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          attempts--;
          continue;
        }

        break;
      }

      if (!savedData) {
        addToDebugLog("Final verification check failed", {
          attempts: 3 - attempts,
          finalData: savedData,
        });
        throw new Error(
          "Data not persisted after multiple verification attempts"
        );
      }

      addToDebugLog("Verification successful", {
        finalData: savedData,
      });

      setProjects(savedData);
      return savedData;
    } catch (error) {
      const errorMessage = `Save operation failed: ${error.message}`;
      setError(errorMessage);
      addToDebugLog("Error in save operation", {
        error: error.message,
        stack: error.stack,
      });
      throw error;
    }
  };

  // Modified handleAddItem with input limiting
  const handleAddItem = async () => {
    if (!newItemName) return;

    if (!isAdmin && inputCount >= 500) {
      setError(
        "Daily input limit reached (500/500). Please try again tomorrow."
      );
      return;
    }

    if (newItemName.length > 50) {
      setError("Item name cannot exceed 100 characters");
      return;
    }

    // Check if we've reached the maximum item limit (108)
    const currentItemCount =
      currentProject && projects[currentProject]
        ? Object.keys(projects[currentProject]).filter(
            (key) => key !== "__PLACEHOLDER__"
          ).length
        : 0;

    if (currentItemCount >= 108) {
      setError("Maximum item limit reached (108/108). Cannot add more items.");
      return;
    }

    const db = getDatabase();
    const userInputRef = ref(
      db,
      getFortpixelUserInputsPath(auth.currentUser.uid),
    );

    try {
      // Update input count for non-approved users
      if (isAdmin) {
        await set(userInputRef, {
          count: inputCount + 1,
          lastReset: lastReset,
        });
      }

      // Create a copy of the current project data
      let projectData = projects[currentProject]
        ? { ...projects[currentProject] }
        : {};

      // Remove placeholder if present
      if (
        Object.keys(projectData).length === 1 &&
        projectData["__PLACEHOLDER__"] === "__EMPTY__"
      ) {
        projectData = {};
      }

      let id = newItemId;
      if (!id) {
        const existingIds = Object.keys(projectData).filter(
          (key) => key !== "__PLACEHOLDER__"
        );
        id =
          existingIds.length > 0
            ? String(
                Math.max(...existingIds.map(Number).filter((n) => !isNaN(n))) +
                  1
              )
            : "0";
      }

      const updatedProjects = {
        ...projects,
        [currentProject]: {
          ...projectData,
          [id]: newItemName,
        },
      };

      await saveData(updatedProjects);
      setNewItemId("");
      setNewItemName("");
      setSearchTerm("");
    } catch (err) {
      console.error("Failed to add item:", err);
      setError(err.message);
    }
  };

  const handleRemoveItem = (id) => {
    // Create a copy of the current project data
    const projectData = { ...projects[currentProject] } || {};

    // Remove the specific item
    delete projectData[id];

    // If no items remain, add a placeholder
    if (Object.keys(projectData).length === 0) {
      projectData["__PLACEHOLDER__"] = "__EMPTY__";
    }

    const updatedProjects = {
      ...projects,
      [currentProject]: projectData,
    };

    saveData(updatedProjects).catch((err) => {
      console.error("Failed to remove item:", err);
    });
  };

  const handleCreateProject = (projectName) => {
    if (!projectName) return;

    if (projects[projectName]) {
      setError("Project already exists");
      return;
    }

    const updatedProjects = {
      ...projects,
      [projectName]: {
        __PLACEHOLDER__: "__EMPTY__", // Add a placeholder to ensure the project exists
      },
    };

    saveData(updatedProjects)
      .then(() => {
        setCurrentProject(projectName);
      })
      .catch((err) => {
        console.error("Failed to create project:", err);
        setError(`Failed to create project: ${err.message}`);
      });
  };

  const handleNewProject = () => {
    const name = prompt("Enter new project name:");
    if (name) handleCreateProject(name);
  };

  const handleDeleteProject = () => {
    if (Object.keys(projects).length <= 1) {
      setError("Cannot delete the last project");
      return;
    }

    const { [currentProject]: deleted, ...remainingProjects } = projects;
    const nextProject = Object.keys(remainingProjects)[0];

    saveData(remainingProjects)
      .then(() => {
        setCurrentProject(nextProject);
      })
      .catch((err) => {
        console.error("Failed to delete project:", err);
      });
  };

  const checkDuplicates = (data) => {
    const nameCount = {};
    const duplicatesList = [];

    Object.entries(data || {}).forEach(([id, name]) => {
      if (name in nameCount) {
        nameCount[name].push(id);
        if (nameCount[name].length === 2) {
          duplicatesList.push([name, nameCount[name]]);
        }
      } else {
        nameCount[name] = [id];
      }
    });

    setDuplicates(duplicatesList);
    setShowDuplicateModal(true); // Always show the modal regardless of duplicates found
  };

  // Modified handleKeyPress to handle search functionality
  const handleKeyPress = (e) => {
    // Check if the pressed key is Enter
    if (e.key === "Enter") {
      if (!hasMultipleTerms && newItemName.trim()) {
        handleAddItem();
      }
    }
  };

  // Handle name input change
  const handleNameInputChange = (e) => {
    const value = e.target.value;
    setNewItemName(value);

    // Always update search term to enable real-time filtering
    setSearchTerm(value);
  };

  const searchTerms = searchTerm
    .split(",")
    .map((term) => term.trim().toLowerCase())
    .filter((term) => term.length > 0);

  // Check if input contains commas (multiple search terms)
  const hasMultipleTerms = newItemName.includes(",");

  // Always filter items based on the input, regardless of search mode
  const filteredItems =
    currentProject && projects[currentProject]
      ? Object.entries(projects[currentProject])
          .filter(([id]) => id !== "__PLACEHOLDER__")
          .filter(
            ([id, name]) =>
              searchTerm.length === 0 || // Show all items if no search term
              searchTerms.some(
                (term) =>
                  id.toLowerCase().includes(term) ||
                  name.toLowerCase().includes(term)
              )
          )
      : [];

  // Get all items for display when search term is empty
  const allItems =
    currentProject && projects[currentProject]
      ? Object.entries(projects[currentProject]).filter(
          ([id]) => id !== "__PLACEHOLDER__"
        )
      : [];

  // Modified handleBulkAdd with permissions
  const handleBulkAdd = async (input) => {
    if (!input) {
      setError("No input provided");
      return;
    }

    const items = input
      .split("\n")
      .map((item) => item.trim())
      .filter((item) => item !== "" && item.length <= 50);

    if (items.length === 0) {
      setError("No valid items to add");
      return;
    }

    try {
      let projectData = projects[currentProject]
        ? { ...projects[currentProject] }
        : {};

      if (
        Object.keys(projectData).length === 1 &&
        projectData["__PLACEHOLDER__"] === "__EMPTY__"
      ) {
        projectData = {};
      }

      // Check if adding these items would exceed the 108 item limit
      const currentItemCount = Object.keys(projectData).filter(
        (key) => key !== "__PLACEHOLDER__"
      ).length;

      if (currentItemCount + items.length > 108) {
        setError(
          `Cannot add ${items.length} items. This would exceed the maximum limit of 108 items. Current count: ${currentItemCount}/108`
        );
        return;
      }

      const existingIds = Object.keys(projectData).filter(
        (key) => key !== "__PLACEHOLDER__"
      );
      let nextId =
        existingIds.length > 0
          ? Math.max(...existingIds.map(Number).filter((n) => !isNaN(n))) + 1
          : 0;

      const newItems = {};
      items.forEach((item) => {
        if (item.length <= 100) {
          newItems[nextId.toString()] = item;
          nextId++;
        }
      });

      // Only add bulk_add flag if user is admin
      const updatedProjects = {
        ...projects,
        [currentProject]: {
          ...projectData,
          ...newItems,
        },
      };

      await saveData(updatedProjects);
    } catch (err) {
      console.error("Failed to bulk add items:", err);
      setError(`Failed to bulk add items: ${err.message}`);
    }
  };

  const handleEditItem = async (id, newName) => {
    if (!newName) return;

    if (!isAdmin && inputCount >= 500) {
      setError(
        "Daily input limit reached (500/500). Please try again tomorrow."
      );
      return;
    }

    if (newName.length > 50) {
      setError("Item name cannot exceed 50 characters");
      return;
    }

    const db = getDatabase();
    const userInputRef = ref(
      db,
      getFortpixelUserInputsPath(auth.currentUser.uid),
    );

    try {
      // Update input count for non-admin users
      if (!isAdmin) {
        await set(userInputRef, {
          count: inputCount + 1,
          lastReset: lastReset,
        });
      }

      // Create a copy of the current project data
      const projectData = { ...projects[currentProject] };

      // Update the item
      const updatedProjects = {
        ...projects,
        [currentProject]: {
          ...projectData,
          [id]: newName,
        },
      };

      await saveData(updatedProjects);
    } catch (err) {
      console.error("Failed to edit item:", err);
      setError(err.message);
    }
  };

  const handleReorderIds = async () => {
    if (!isAdmin) {
      setError("Unauthorized: Reordering is restricted to admins");
      return;
    }

    try {
      const projectData = { ...projects[currentProject] };

      // Filter out placeholder and create array of entries
      const entries = Object.entries(projectData)
        .filter(([key]) => key !== "__PLACEHOLDER__")
        .sort((a, b) => {
          // Try to sort numerically first
          const numA = parseInt(a[0]);
          const numB = parseInt(b[0]);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          // Fall back to string comparison
          return a[0].localeCompare(b[0]);
        });

      // Create new object with reordered IDs
      const reorderedData = {};
      entries.forEach(([_, value], index) => {
        reorderedData[index.toString()] = value;
      });

      // Update the projects data
      const updatedProjects = {
        ...projects,
        [currentProject]: reorderedData,
      };

      await saveData(updatedProjects);
      addToDebugLog("IDs reordered successfully", {
        projectName: currentProject,
        itemCount: entries.length,
      });
    } catch (err) {
      console.error("Failed to reorder IDs:", err);
      setError(`Failed to reorder IDs: ${err.message}`);
      addToDebugLog("Reorder IDs failed", {
        error: err.message,
        stack: err.stack,
      });
    }
  };

  // Add keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+F or Cmd+F (Mac) to focus the input field
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault(); // Prevent browser's default search
        if (nameInputRef.current) {
          nameInputRef.current.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6">
      {/* Modals */}
      {showBulkAddModal && isAdmin && (
        <BulkAddModal
          onAdd={handleBulkAdd}
          onClose={() => setShowBulkAddModal(false)}
        />
      )}
      {showDuplicateModal && (
        <DuplicateModal
          duplicates={duplicates}
          onClose={() => setShowDuplicateModal(false)}
        />
      )}
      {showJsonModal && isAdmin && (
        <JsonModal
          data={projects[currentProject] || {}}
          onClose={() => setShowJsonModal(false)}
        />
      )}

      {/* Project Controls */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <select
          value={currentProject}
          onChange={(e) => setCurrentProject(e.target.value)}
          className="w-full sm:w-auto p-2 rounded bg-gray-800 text-white"
        >
          {Object.keys(projects).map((project) => (
            <option key={project} value={project}>
              {project}
            </option>
          ))}
        </select>
        <button
          onClick={handleNewProject}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
        >
          New Project
        </button>
        {isAdmin && (
          <>
            <button
              onClick={() => {
                if (Object.keys(projects).length <= 1) {
                  setError("Cannot delete the last project");
                  return;
                }
                if (
                  window.confirm(
                    `Are you sure you want to delete "${currentProject}"?`
                  )
                ) {
                  handleDeleteProject();
                }
              }}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              disabled={Object.keys(projects).length <= 1}
            >
              Delete Project
            </button>
            <button
              onClick={() => setShowBulkAddModal(true)}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
            >
              Bulk Add
            </button>
            <button
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to reorder all IDs? This action cannot be undone."
                  )
                ) {
                  handleReorderIds();
                }
              }}
              className="w-full sm:w-auto px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
            >
              Reorder IDs
            </button>
          </>
        )}
      </div>

      {/* Status displays */}
      <div className="flex flex-col gap-1 sm:flex-row sm:justify-between text-sm text-gray-400">
        {!isAdmin && <div>Daily inputs: {inputCount}/500</div>}
        <div>
          Items:{" "}
          {currentProject && projects[currentProject]
            ? Object.keys(projects[currentProject]).filter(
                (key) => key !== "__PLACEHOLDER__"
              ).length
            : 0}
          /108
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="space-y-4">
          <div className="p-4 mb-4 text-red-500 bg-red-100 rounded">
            {error}
          </div>
          <div className="p-4 bg-gray-900 rounded overflow-x-auto">
            <h3 className="text-lg font-bold mb-2">Debug Log:</h3>
            {debugLog.map((entry, index) => (
              <div key={index} className="mb-2 font-mono text-sm">
                <div className="text-gray-400">{entry.timestamp}</div>
                <div className="text-white">{entry.message}</div>
                {entry.data && (
                  <pre className="text-gray-300 mt-1 overflow-x-auto">
                    {entry.data}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Integrated Search and Add Item Controls */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <input
            type="text"
            value={newItemId}
            onChange={(e) => setNewItemId(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="ID (optional)"
            className="w-full sm:w-40 p-2 rounded bg-gray-800 text-white"
          />
          <div className="relative flex-1">
            <input
              ref={nameInputRef}
              type="text"
              value={newItemName}
              onChange={handleNameInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Create or search (comma-separated for multiple terms)"
              className="w-full p-2 rounded bg-gray-800 text-white flex-1"
              maxLength={hasMultipleTerms ? undefined : 50}
            />
          </div>
          <button
            onClick={handleAddItem}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={hasMultipleTerms || !newItemName.trim()}
            title={
              hasMultipleTerms
                ? "Only one word at a time allowed for adding"
                : "Add item"
            }
          >
            Add
          </button>
        </div>
      </div>

      {/* Virtual List - show filtered items when searching, all items otherwise */}
      <VirtualList
        items={searchTerm ? filteredItems : allItems}
        onRemove={handleRemoveItem}
        onEdit={handleEditItem}
      />

      {/* Bottom Actions */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        {isAdmin && (
          <button
            onClick={() => setShowJsonModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm"
          >
            View JSON
          </button>
        )}
        <button
          onClick={() => checkDuplicates(projects[currentProject])}
          className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
        >
          Check Duplicates
        </button>
        <span className="text-white">Duplicates: {duplicates.length}</span>
      </div>
    </div>
  );
};

export default DataManager;
